import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, ChevronLeft, ChevronRight, Loader2, MessageCircle, Quote, Star, X } from "lucide-react";
import { Overline, Reveal } from "./Reveal";
import { waLink, WA_MESSAGES } from "../config/site";
import { trackEvent } from "../lib/analytics";
import { api, formatApiError } from "../lib/api";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-gb-white placeholder:text-gb-gray/50 transition-colors focus:border-gb-silver/50 focus:outline-none";

const Stars = ({ value, size = "h-3.5 w-3.5" }) => (
  <div className="flex gap-1" aria-label={`Avaliação ${value} de 5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${size} ${i < value ? "fill-gb-silver text-gb-silver" : "text-gb-silver/35"}`} aria-hidden="true" />
    ))}
  </div>
);

const ReviewForm = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", business: "", rating: 0, comment: "" });
  const [hover, setHover] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) return setError("Informe seu nome.");
    if (form.rating < 1) return setError("Escolha uma nota de 1 a 5 estrelas.");
    if (form.comment.trim().length < 4) return setError("Escreva um comentário sobre o trabalho.");
    setSending(true);
    try {
      await api.post("/reviews", {
        name: form.name.trim(),
        business: form.business.trim(),
        rating: form.rating,
        comment: form.comment.trim(),
      });
      trackEvent("review_submit", { rating: form.rating });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Avaliar o trabalho da GOBAX"
      data-testid="review-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.96 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="card-chrome w-full max-w-md rounded-2xl p-7 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {sent ? (
          <div className="flex flex-col items-center py-8 text-center" data-testid="review-success">
            <CheckCircle2 className="h-11 w-11 text-gb-silverhi" aria-hidden="true" />
            <h3 className="mt-5 font-display text-xl font-bold">Avaliação recebida!</h3>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-gb-gray">
              Obrigado pela confiança. Sua avaliação aparecerá aqui no site após a moderação da equipe.
            </p>
            <button type="button" onClick={onClose} data-testid="review-close-success" className="btn-ghost-chrome mt-7 rounded-full px-6 py-2.5 text-xs font-semibold text-gb-white">
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate data-testid="review-form" className="space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl font-bold">Avalie nosso trabalho</h3>
                <p className="mt-1 text-xs text-gb-gray">Conte como foi sua experiência com a GOBAX</p>
              </div>
              <button type="button" onClick={onClose} aria-label="Fechar" data-testid="review-close-btn" className="rounded-full border border-white/10 p-2 text-gb-gray transition-colors hover:text-gb-white">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Nota de 1 a 5 estrelas">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={form.rating === n}
                  aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
                  data-testid={`review-star-${n}`}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setForm((f) => ({ ...f, rating: n }))}
                  className="p-1 transition-transform duration-200 hover:scale-110"
                >
                  <Star className={`h-7 w-7 transition-colors ${n <= (hover || form.rating) ? "fill-gb-silverhi text-gb-silverhi" : "text-gb-silver/35"}`} aria-hidden="true" />
                </button>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <input type="text" placeholder="Seu nome" aria-label="Seu nome" data-testid="review-name-input" className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <input type="text" placeholder="Seu negócio (opcional)" aria-label="Seu negócio" data-testid="review-business-input" className={inputCls} value={form.business} onChange={(e) => setForm((f) => ({ ...f, business: e.target.value }))} />
            </div>
            <textarea rows={3} placeholder="Como foi trabalhar com a GOBAX?" aria-label="Comentário" data-testid="review-comment-input" className={`${inputCls} resize-none`} value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} />

            {error && (
              <p role="alert" data-testid="review-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-300">
                {error}
              </p>
            )}
            <button type="submit" disabled={sending} data-testid="review-submit-btn" className="btn-chrome inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold disabled:opacity-60">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Enviar avaliação
            </button>
            <p className="text-center font-mono text-[9px] uppercase tracking-[0.2em] text-gb-gray/60">
              Publicada após moderação da equipe GOBAX
            </p>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
};

export const Testimonials = () => {
  const [reviews, setReviews] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !reviews || reviews.length < 2) return undefined;
    emblaApi.on("select", onSelect);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduce) timer.current = setInterval(() => emblaApi.scrollNext(), 5200);
    return () => clearInterval(timer.current);
  }, [emblaApi, onSelect, reviews]);

  const hasReviews = reviews && reviews.length > 0;

  return (
    <section data-testid="testimonials-section" aria-label="Avaliações de clientes" className="relative py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <Overline>Avaliações</Overline>
            <h2 className="mt-5 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
              O que dizem sobre <span className="chrome-text">trabalhar com a GOBAX</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="flex gap-3">
            {hasReviews && (
              <>
                <button type="button" aria-label="Avaliação anterior" data-testid="testimonial-prev-btn" onClick={() => emblaApi && emblaApi.scrollPrev()} className="btn-ghost-chrome flex h-11 w-11 items-center justify-center rounded-full text-gb-white">
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <button type="button" aria-label="Próxima avaliação" data-testid="testimonial-next-btn" onClick={() => emblaApi && emblaApi.scrollNext()} className="btn-ghost-chrome flex h-11 w-11 items-center justify-center rounded-full text-gb-white">
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </>
            )}
            <button type="button" onClick={() => setShowForm(true)} data-testid="review-open-btn" className="btn-chrome inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold">
              <Star className="h-3.5 w-3.5" aria-hidden="true" />
              Avaliar nosso trabalho
            </button>
          </Reveal>
        </div>

        {hasReviews ? (
          <Reveal delay={0.15}>
            <div className="mt-12 overflow-hidden" ref={emblaRef} data-testid="testimonials-carousel">
              <div className="flex">
                {reviews.map((r, i) => (
                  <div key={r.id} className="min-w-0 flex-[0_0_100%] pr-4 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] lg:pr-6">
                    <figure data-testid={`review-card-${i}`} className="card-glass flex h-full flex-col p-7">
                      <Quote className="card-glass-icon h-6 w-6 opacity-70" aria-hidden="true" />
                      <div className="mt-4">
                        <Stars value={r.rating} />
                      </div>
                      <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-gb-silver/90">“{r.comment}”</blockquote>
                      <figcaption className="mt-6 border-t border-white/8 pt-4">
                        <p className="card-glass-title font-display text-sm font-bold">{r.name}</p>
                        {r.business && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gb-gray">{r.business}</p>}
                      </figcaption>
                    </figure>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.15}>
            <div data-testid="testimonials-empty-state" className="card-chrome mt-12 flex flex-col items-center rounded-2xl border-dashed px-8 py-16 text-center sm:py-20">
              <Stars value={0} size="h-5 w-5" />
              <h3 className="mt-7 max-w-lg font-display text-xl font-bold leading-snug sm:text-2xl">
                Este espaço guarda as palavras de quem confiar na nossa estratégia.
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-gb-gray sm:text-base">
                Estamos construindo nossa história agora — e cada cliente que fecha com a GOBAX pode
                deixar aqui sua avaliação real. Sem depoimentos inventados: só o que nosso trabalho conquistar.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a
                  href={waLink(WA_MESSAGES.default)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { location: "testimonials" })}
                  data-testid="testimonials-cta"
                  className="btn-chrome inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
                >
                  <MessageCircle className="h-4 w-4" aria-hidden="true" />
                  Quero ser o próximo case
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
                <button type="button" onClick={() => setShowForm(true)} data-testid="testimonials-rate-btn" className="btn-ghost-chrome inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-gb-white">
                  <Star className="h-4 w-4" aria-hidden="true" />
                  Já sou cliente — avaliar
                </button>
              </div>
            </div>
          </Reveal>
        )}
      </div>

      <AnimatePresence>{showForm && <ReviewForm onClose={() => setShowForm(false)} />}</AnimatePresence>
    </section>
  );
};
